<template>
  <div class="d3-sunburst">
    <v-container id="sunburst-container" grid-list-md fill-height text-xs-center>
      <v-layout row wrap>
        <v-flex xs12>
          <v-btn-toggle mandatory>
            <v-btn
              @click="onBtnClicked('aiib')"
              color="white"
              :ripple="false"
              class="btn--plain"
              :class="{ 'v-btn--active': activeBtn === 'aiib' }">
              AIIB
            </v-btn>
            <v-btn
              @click="onBtnClicked('imf')"
              color="white"
              :ripple="false"
              class="btn--plain"
              :class="{ 'v-btn--active': activeBtn === 'imf' }">
              IMF
            </v-btn>
            <v-btn
              @click="onBtnClicked('ibrd')"
              color="white"
              :ripple="false"
              class="btn--plain"
              :class="{ 'v-btn--active': activeBtn === 'ibrd' }">
              IBRD
            </v-btn>
            <v-btn
              @click="onBtnClicked('adb')"
              color="white"
              :ripple="false"
              class="btn--plain"
              :class="{ 'v-btn--active': activeBtn === 'adb' }">
              ADB
            </v-btn>
          </v-btn-toggle>
        </v-flex>

        <v-flex xs12>
          <div id="sunburst" />
        </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import * as d3 from 'd3';

export default {
  name: 'D3Sunburst',
  data() {
    return {
      data: null,
      rawData: null,
      root: null,
      settings: {
        w: 0,
        h: 0,
        radius: 0,
      },
      theContainer: {
        svg: null,
        chart: null,
        path: null,
        label: null,
        parent: {
          g: null,
          circle: null,
          regionTxt: null,
          memberTxt: null,
          percentageTxt: null,
        },
      },
      arc: null,
      activeBtn: 'aiib',
    };
  },
  async mounted() {
    // Dimensions of sunburst
    this.settings.w = 940;
    this.settings.h = this.settings.w;
    this.settings.radius = Math.min(this.settings.w, this.settings.h) / 6;

    // Load the data
    this.rawData = {};
    this.rawData.imf = await d3.csv('/data/finance/imf.csv');
    this.rawData.ibrd = await d3.csv('/data/finance/ibrd.csv');
    this.rawData.adb = await d3.csv('/data/finance/adb.csv');
    this.rawData.aiib = await d3.csv('/data/finance/aiib.csv');
    this.data = this.generateData(this.rawData.aiib);

    this.init();
  },
  methods: {
    init() {
      // Create primary <g> el
      this.theContainer.svg = d3
        .select('#sunburst')
        .append('svg')
        .attr('preserveAspectRatio', 'xMidYMid meet')
        .attr('viewBox', `0 0 ${this.settings.w} ${this.settings.h}`);
      this.theContainer.chart = this.theContainer.svg
        .append('g')
        .attr('transform', `translate(${this.settings.w / 2}, ${this.settings.h / 2})`);

      this.root = this.partition(this.data);

      this.root.each(d => (d.current = d));

      this.arc = d3
        .arc()
        .startAngle(d => d.x0)
        .endAngle(d => d.x1)
        .padAngle(d => Math.min((d.x1 - d.x0) / 2, 0.005))
        .padRadius(this.settings.radius * 1.5)
        .innerRadius(d => d.y0 * this.settings.radius)
        .outerRadius(d => Math.max(d.y0 * this.settings.radius, d.y1 * this.settings.radius - 1));

      this.theContainer.path = this.theContainer.chart
        .append('g')
        .selectAll('path')
        .data(this.root.descendants())
        .enter()
        .append('path')
        .attr('fill', this.color)
        .attr('fill-opacity', d => (this.arcVisible(d.current) ? (d.children ? 0.6 : 0.4) : 0))
        .attr('d', d => this.arc({ x0: 0, x1: 0, y0: 0, y1: 0 }))
        .attr('data-id', d => d.current.data.name);

      this.theContainer.path.on('mouseover', this.onMouseovered).on('mouseout', this.onMouseout);

      this.theContainer.path
        .filter(d => d.children)
        .style('cursor', 'pointer')
        .on('click', this.onClicked);

      const format = d3.format(',d');

      this.theContainer.path.append('title').text(
        d =>
          `${d
            .ancestors()
            .map(d => d.data.name)
            .reverse()
            .join('/')}\n${format(d.value)}`
      );

      this.theContainer.label = this.theContainer.chart
        .append('g')
        .attr('pointer-events', 'none')
        .attr('text-anchor', 'middle')
        .style('user-select', 'none')
        .selectAll('text')
        .data(this.root.descendants())
        .enter()
        .append('text')
        .attr('dy', '0.35em')
        .attr('fill-opacity', d => +this.labelVisible({ x0: 0, x1: 0, y0: 0, y1: 0 }))
        .attr('transform', d => this.labelTransform({ x0: 0, x1: 0, y0: 0, y1: 0 }))
        .attr('fill', '#fff')
        .text(d => d.data.name);

      const t = this.theContainer.chart.transition().duration(750);
      const self = this;
      this.theContainer.path
        .transition(t)
        .tween('data', d => {
          const i = d3.interpolate({ x0: 0, x1: 0, y0: 0, y1: 0 }, d.current);
          return t => (d.current = i(t));
        })
        .filter(function(d) {
          return +this.getAttribute('fill-opacity') || self.arcVisible(d.current);
        })
        .attr('fill-opacity', d => (this.arcVisible(d.current) ? (d.children ? 0.6 : 0.4) : 0))
        .attrTween('d', d => () => this.arc(d.current));
      this.theContainer.label
        .filter(function(d) {
          return +this.getAttribute('fill-opacity') || self.labelVisible(d.current);
        })
        .transition(t)
        .attr('fill-opacity', d => +this.labelVisible(d.current))
        .attrTween('transform', d => () => this.labelTransform(d.current));

      this.theContainer.parent.g = this.theContainer.chart.append('g');
      this.theContainer.parent.circle = this.theContainer.parent.g
        .append('circle')
        .datum(this.root)
        .attr('r', this.settings.radius)
        .attr('fill', 'none')
        .attr('pointer-events', 'all')
        .on('click', this.onClicked);
      this.theContainer.parent.regionTxt = this.theContainer.parent.g
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('fill', 'rgba(0, 0, 0, 0.7)')
        .attr('font-family', "'LL Akkurat Pro', Helvetica, sans-serif")
        .attr('font-size', 18)
        .attr('line-height', 25);
      this.theContainer.parent.memberTxt = this.theContainer.parent.g
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('fill', 'rgba(0, 0, 0, 0.7)')
        .attr('font-family', "'LL Akkurat Pro', Helvetica, sans-serif")
        .attr('font-size', 13)
        .attr('line-height', 18);
      this.theContainer.parent.percentageTxt = this.theContainer.parent.g
        .append('text')
        .attr('text-anchor', 'middle')
        .attr('fill', '#F38063')
        .attr('font-weight', 600)
        .attr('font-family', "'LL Akkurat Pro', Helvetica, sans-serif")
        .attr('font-size', 13)
        .attr('line-height', 18);
    },
    removeAll() {
      this.theContainer.svg
        .transition()
        .duration(500)
        .style('opacity', 0)
        .remove();
    },
    generateData(rawData) {
      const data = {
        name: 'BRI',
        children: [
          {
            name: 'Asia',
            children: [],
          },
          {
            name: 'Europe',
            children: [],
          },
          {
            name: 'Africa',
            children: [],
          },
          {
            name: 'North America',
            children: [],
          },
          {
            name: 'South America',
            children: [],
          },
          {
            name: 'Oceania',
            children: [],
          },
        ],
      };

      // Asia
      data.children[0].children = rawData.filter(d => d.region === 'Asia');
      // Europe
      data.children[1].children = rawData.filter(d => d.region === 'Europe');
      // Africa
      data.children[2].children = rawData.filter(d => d.region === 'Africa');
      // North America
      data.children[3].children = rawData.filter(d => d.region === 'North America');
      // South America
      data.children[4].children = rawData.filter(d => d.region === 'South America');
      // Oceania
      data.children[5].children = rawData.filter(d => d.region === 'Oceania');

      return data;
    },
    onBtnClicked(param) {
      // Return if activeBtn is clicked
      if (this.activeBtn === param) {
        return;
      }

      // Back to parent
      this.onClicked(this.root);

      const t = this.theContainer.chart.transition().duration(750);
      const self = this;
      this.theContainer.path
        .transition(t)
        .tween('data', d => {
          const i = d3.interpolate(d.current, { x0: 0, x1: 0, y0: 0, y1: 0 });
          return t => (d.current = i(t));
        })
        .filter(function(d) {
          return +this.getAttribute('fill-opacity') || self.arcVisible({ x0: 0, x1: 0, y0: 0, y1: 0 });
        })
        .attr('fill-opacity', d => (this.arcVisible({ x0: 0, x1: 0, y0: 0, y1: 0 }) ? (d.children ? 0.6 : 0.4) : 0))
        .attrTween('d', d => () => this.arc(d.current));
      this.theContainer.label
        .filter(function(d) {
          return +this.getAttribute('fill-opacity') || self.labelVisible({ x0: 0, x1: 0, y0: 0, y1: 0 });
        })
        .transition(t)
        .attr('fill-opacity', d => +this.labelVisible({ x0: 0, x1: 0, y0: 0, y1: 0 }))
        .attrTween('transform', d => () => this.labelTransform(d.current));

      this.theContainer.svg
        .transition()
        .delay(500)
        .remove();

      // Load the data
      switch (param) {
        case 'aiib':
          this.data = this.generateData(this.rawData.aiib);
          break;

        case 'ibrd':
          this.data = this.generateData(this.rawData.ibrd);
          break;

        case 'adb':
          this.data = this.generateData(this.rawData.adb);
          break;

        default:
          // imf
          // Load the data
          this.data = this.generateData(this.rawData.imf);
          break;
      }

      this.activeBtn = param;

      // Rebuild all
      setTimeout(() => {
        this.init();
      }, 500);
    },
    color(d) {
      while (d.depth > 1) d = d.parent;
      if (d.data.name === 'Asia') {
        return '#EB6468';
      } else if (d.data.name === 'Europe') {
        return '#744465';
      } else if (d.data.name === 'North America') {
        return '#5150A4';
      } else if (d.data.name === 'South America') {
        return '#5B95A6';
      } else if (d.data.name === 'Africa') {
        return '#40C88F';
      }
      // Oceania
      return '#378572';
    },
    onClicked(p) {
      this.theContainer.parent.circle.datum(p.parent || this.root);

      this.root.each(
        d =>
          (d.target = {
            x0: Math.max(0, Math.min(1, (d.x0 - p.x0) / (p.x1 - p.x0))) * 2 * Math.PI,
            x1: Math.max(0, Math.min(1, (d.x1 - p.x0) / (p.x1 - p.x0))) * 2 * Math.PI,
            y0: Math.max(0, d.y0 - p.depth),
            y1: Math.max(0, d.y1 - p.depth),
          })
      );

      // Clear parent texts if exist
      this.theContainer.parent.regionTxt.text('');
      this.theContainer.parent.memberTxt.text('');
      this.theContainer.parent.percentageTxt.text('');

      const t = this.theContainer.chart.transition().duration(750);

      // Transition the data on all arcs, even the ones that aren’t visible,
      // so that if this transition is interrupted, entering arcs will start
      // the next transition from the desired position.
      const self = this;
      this.theContainer.path
        .transition(t)
        .tween('data', d => {
          const i = d3.interpolate(d.current, d.target);
          return t => (d.current = i(t));
        })
        .filter(function(d) {
          return +this.getAttribute('fill-opacity') || self.arcVisible(d.target);
        })
        .attr('fill-opacity', d => (this.arcVisible(d.target) ? (d.children ? 0.6 : 0.4) : 0))
        .attrTween('d', d => () => this.arc(d.current));

      this.theContainer.label
        .filter(function(d) {
          return +this.getAttribute('fill-opacity') || self.labelVisible(d.target);
        })
        .transition(t)
        .attr('fill-opacity', d => +this.labelVisible(d.target))
        .attrTween('transform', d => () => this.labelTransform(d.current));
    },
    onMouseovered(p) {
      if (p.depth < 2 && p.depth > 0) {
        // Region
        this.theContainer.parent.regionTxt.text(p.data.name);
        this.theContainer.parent.percentageTxt.attr('y', '1.6em').text(`${p.value}%`);
      } else {
        this.theContainer.parent.regionTxt.text(p.parent.data.name);
        this.theContainer.parent.memberTxt.attr('y', '1.6em').text(p.data.name);
        this.theContainer.parent.percentageTxt.attr('y', '3em').text(`${p.value}%`);
      }
    },
    onMouseout(p) {
      this.theContainer.parent.regionTxt.text('');
      this.theContainer.parent.memberTxt.text('');
      this.theContainer.parent.percentageTxt.text('');
    },
    arcVisible(d) {
      return d.y1 <= 3 && d.y0 >= 1 && d.x1 > d.x0;
    },
    labelVisible(d) {
      return d.y1 <= 3 && d.y0 >= 1 && (d.y1 - d.y0) * (d.x1 - d.x0) > 0.03;
    },
    labelTransform(d) {
      const x = (((d.x0 + d.x1) / 2) * 180) / Math.PI;
      const y = ((d.y0 + d.y1) / 2) * this.settings.radius;
      return `rotate(${x - 90}) translate(${y},0) rotate(${x < 180 ? 0 : 180})`;
    },
    partition(data) {
      const root = d3
        .hierarchy(data)
        .sum(d => d.size)
        .sort((a, b) => b.value - a.value);
      return d3.partition().size([2 * Math.PI, root.height + 1])(root);
    },
  },
};
</script>

<style lang="stylus" scoped>
#sunburst-container {
  .btn--plain {
    &.v-btn--active, &:hover, &:focus {
      color: #F2784B;

      &:before {
        background-color: transparent;
      }
    }
  }
}
</style>
